import { Module } from '@/types'
import { CardBase, CardBaseProps, Icon, Image, Text, View } from '@/components'
import { arePropsEqual, assignTextStyle, TypeGuards } from '@codeleap/common'
import { CARD_BASE_HEIGHT, React, variantProvider } from '@/app'
import { format } from 'date-fns'

const ICON_SIZE = 12
const IMAGE_WIDTH = '25%'

type BetaGameCardProp = Omit<CardBaseProps, 'debugName'> & {
  module: Module
}

function BetaGameCardComponent(props: BetaGameCardProp) {
  const { module, disabled, ...cardProps } = props

  const formattedDate = format(new Date(), 'MMM dd')

  const hasImage = TypeGuards.isString(module?.image)

  return (
    <CardBase debugName={`beta-game-card=${module.title}`} variants={['card:collection']} {...cardProps} gap={0}>
      {hasImage ? (
        <View style={styles.imageWrapper}>
          <Image objectFit='cover' source={module?.image} style={styles.image} />
        </View>
      ) : null}

      <View variants={['column', 'alignStart', 'justifyStart', 'padding:2', 'fullWidth', 'flex', 'fullHeight']}>
        <View variants={['row', 'fullWidth', 'justifySpaceBetween', 'alignCenter']}>
          <View variants={['row', 'center']}>
            <Icon debugName='calendar_icon' name='calendar' size={ICON_SIZE} variants={['neutral5']} />
            <Text variants={['p4', 'color:neutral5', 'marginLeft:0.5']} text={formattedDate} />
          </View>
          {module?.beta_game ? <Text text={'Beta'} style={styles.isNewBadge} /> : null}
        </View>

        <Text variants={['h5', 'color:neutral6', 'marginTop:1']} text={module?.name} style={styles.text} />
      </View>
    </CardBase>
  )
}

export const BetaGameCard = React.memo(BetaGameCardComponent, (previous, next) => {
  return arePropsEqual(previous, next, { check: ['module'] })
})

const styles = variantProvider.createComponentStyle(theme => ({
  imageWrapper: {
    height: 'auto',
    minWidth: IMAGE_WIDTH,
    maxWidth: IMAGE_WIDTH,
    flex: 1,
    overflow: 'hidden',
    maxHeight: CARD_BASE_HEIGHT.collection,
    minHeight: CARD_BASE_HEIGHT.collection,
  },
  image: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    borderBottomLeftRadius: theme.borderRadius.small,
    borderTopLeftRadius: theme.borderRadius.small,
  },
  isNewBadge: {
    backgroundColor: theme.colors.primary3,
    ...assignTextStyle('p2')(theme).text,
    color: theme.colors.neutral1,
    borderRadius: theme.borderRadius.small,
    ...theme.spacing.paddingHorizontal(0.75),
    ...theme.spacing.paddingVertical(0.375),
    ...theme.spacing.marginLeft(0.5),
  },
  text: {
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '3',
  },
}), true)
